import React, { useState, useRef } from "react";
import { api } from "../lib/api";
import InvoiceForm from "../components/InvoiceForm";
import { newInvoice } from "../components/InvoiceForm";
import { ScanLine, FileText, ShieldCheck, Loader2, Camera, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-3 font-semibold transition-colors ${active ? "bg-navy text-white" : "bg-white text-navy border border-slate-200"}`}>
      <Icon className="h-5 w-5" /> {children}
    </button>
  );
}

export default function Scan() {
  const [tab, setTab] = useState("invoice");
  const [loading, setLoading] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState(null);
  const [haccp, setHaccp] = useState(null);
  const [savingHaccp, setSavingHaccp] = useState(false);
  const fileRef = useRef();

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      if (tab === "invoice") {
        const { data } = await api.post("/ai/scan-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const draft = { ...newInvoice(), ...data,
          supplier_name: data.supplier || "",
          date: data.date || new Date().toISOString().slice(0, 10),
          currency: data.currency || "EUR",
          is_credit_note: !!data.is_credit_note,
          line_items: (data.line_items && data.line_items.length ? data.line_items : newInvoice().line_items).map((l) => ({
            product: l.product || "", quantity: l.quantity || 1, unit: l.unit || "pz",
            price: l.price || 0, category_id: l.category_id || null,
          })),
        };
        setInvoiceDraft(draft);
        toast.success("Dati estratti — verifica e conferma");
      } else {
        const { data } = await api.post("/ai/scan-haccp", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setHaccp({ product: data.product || "", ingredients: data.ingredients || "", allergens: data.allergens || [], expiry: data.expiry || "", lot: data.lot || "", storage: data.storage || "", photo_file_id: data.photo_file_id });
        toast.success("Etichetta letta — verifica e salva");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scansione non riuscita");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const saveHaccp = async () => {
    setSavingHaccp(true);
    try {
      await api.post("/haccp", haccp);
      toast.success("Etichetta salvata nel registro HACCP");
      setHaccp(null);
    } catch (e) { toast.error("Errore nel salvataggio"); }
    finally { setSavingHaccp(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Assistente AI</h1>
        <p className="text-slate-500">Scansiona e verifica — l'AI compila, tu confermi in 10 secondi</p>
      </div>

      <div className="flex gap-2">
        <TabButton testid="tab-invoice" active={tab === "invoice"} onClick={() => setTab("invoice")} icon={FileText}>Fattura</TabButton>
        <TabButton testid="tab-haccp" active={tab === "haccp"} onClick={() => setTab("haccp")} icon={ShieldCheck}>Etichetta HACCP</TabButton>
      </div>

      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="scan-file-input" />

      <button
        data-testid="scan-upload-btn"
        onClick={pick}
        disabled={loading}
        className="flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-gold bg-gold/5 py-16 transition-colors hover:bg-gold/10 disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-gold" />
            <span className="font-semibold text-navy">Analisi AI in corso…</span>
          </>
        ) : (
          <>
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gold text-black">
              {tab === "invoice" ? <ScanLine className="h-10 w-10" /> : <Camera className="h-10 w-10" />}
            </div>
            <span className="font-head text-lg font-bold text-navy">
              {tab === "invoice" ? "Fotografa la fattura" : "Fotografa l'etichetta"}
            </span>
            <span className="text-sm text-slate-500">Tocca per scattare o caricare una foto</span>
          </>
        )}
      </button>

      {haccp && (
        <div className="animate-slide-up rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2 text-navy">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <h3 className="font-head text-lg font-bold">Verifica etichetta HACCP</h3>
          </div>
          <div className="space-y-3">
            <Field label="Prodotto" value={haccp.product} onChange={(v) => setHaccp({ ...haccp, product: v })} testid="haccp-product" />
            <Field label="Ingredienti" value={haccp.ingredients} onChange={(v) => setHaccp({ ...haccp, ingredients: v })} testid="haccp-ingredients" />
            <Field label="Allergeni (separati da virgola)" value={(haccp.allergens || []).join(", ")} onChange={(v) => setHaccp({ ...haccp, allergens: v.split(",").map((x) => x.trim()).filter(Boolean) })} testid="haccp-allergens" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Scadenza" type="date" value={haccp.expiry} onChange={(v) => setHaccp({ ...haccp, expiry: v })} testid="haccp-expiry" />
              <Field label="Lotto" value={haccp.lot} onChange={(v) => setHaccp({ ...haccp, lot: v })} testid="haccp-lot" />
            </div>
            <Field label="Conservazione" value={haccp.storage} onChange={(v) => setHaccp({ ...haccp, storage: v })} testid="haccp-storage" />
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={() => setHaccp(null)} className="h-14 flex-1 rounded-lg bg-slate-100 font-semibold text-navy">Annulla</button>
            <button data-testid="save-haccp-btn" onClick={saveHaccp} disabled={savingHaccp} className="flex h-14 flex-[2] items-center justify-center gap-2 rounded-lg bg-gold font-bold text-black hover:bg-gold-hover disabled:opacity-60">
              {savingHaccp && <Loader2 className="h-5 w-5 animate-spin" />} Salva nel registro
            </button>
          </div>
        </div>
      )}

      {invoiceDraft && (
        <InvoiceForm initial={invoiceDraft} onClose={() => setInvoiceDraft(null)} onSaved={() => toast.success("Fattura salvata")} />
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-navy">{label}</label>
      <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
    </div>
  );
}
